import axios from 'axios';

import {getInstance} from './axios-helper';
import {leaveBreadcrumb} from './error-reporter';
import {redirectTo} from '../components/base/routing/partials/routing-util';
import type {Language} from './i18n';
import {t} from './i18n';
import type {Environment, Theme} from './session';
import {
    clearSession,
    clearTokens,
    defaultTheme,
    getEnvironment,
    getPickedLanguage,
    getPickedTheme,
    getPolyshoppenUser,
    setEnvironment,
    setLocale,
    setPickedLanguage,
    setPickedTheme,
    setPolyshoppenUser,
    setTheme,
    setTokens,
} from './session';
import type {User} from '../types/user';

/** How long a login waits for the user's locale; a slower answer still changes the language once it is there. */
const localeTimeout = 3000;

/**
 * Take over the locale of the logged in user (`GET /api/v2/whoami`), which the terminal speaks from then on (see
 * `helpers/i18n.ts`). The user list gave one already (see `pickUser`), but this one is authoritative: it also replaces
 * the one of the stored user. A language the user picked on this terminal (`pickLanguage`) still goes first. Never
 * rejects: without an answer, or without a locale in it (an older API), the terminal keeps the language it speaks.
 */
export const loadLocale = async (environment: Environment): Promise<void> => {
    const user = getPolyshoppenUser();

    try {
        const {data} = await getInstance(environment).get<{locale?: unknown}>('/api/v2/whoami');

        // the user logged out, or another one logged in, while the request was underway
        if (null === user || user.id !== getPolyshoppenUser()?.id) {
            return;
        }

        if ('string' === typeof data?.locale && '' !== data.locale) {
            setLocale(getPickedLanguage(user.id) ?? data.locale);

            if (data.locale !== user.locale) {
                setPolyshoppenUser({...user, locale: data.locale});
            }
        }
    } catch {
        // keep the language: the login does not depend on it
    }
};

/**
 * A user was picked on the login page: speak their language from now on, so the PIN modal and its errors are in it,
 * and show their theme. A user list without locales (an older API) leaves the language as it is, until the login tells
 * it (`loadLocale`). A user that never picked a theme gets the default one, not the one of whoever was here before.
 */
export const pickUser = (user: User): void => {
    const language = getPickedLanguage(user.id) ?? user.locale;

    if (language) {
        setLocale(language);
    }

    setTheme(getPickedTheme(user.id) ?? defaultTheme);
};

/**
 * The logged in user picked a language on this terminal, over the locale of their account; `null` follows that locale
 * again. Kept on this terminal only: other terminals and apps keep speaking the locale of the account.
 */
export const pickLanguage = (language: Language | null): void => {
    const user = getPolyshoppenUser();

    if (null === user) {
        return;
    }

    setPickedLanguage(user.id, language);

    const locale = language ?? user.locale;

    if (locale) {
        setLocale(locale);
    }
};

/**
 * The logged in user picked a theme on this terminal (F8 in the top bar). Like a picked language it is kept on this
 * terminal only, and stays for the login page after they leave.
 */
export const pickTheme = (theme: Theme): void => {
    const user = getPolyshoppenUser();

    if (null === user) {
        return;
    }

    setPickedTheme(user.id, theme);
    setTheme(theme);
};

/**
 * Log in a warehouse user with its PIN: obtain a JWT from the API of the given environment and store it, together
 * with the user, in the session. Then the user's locale is fetched, for a moment at most: the terminal opens in the
 * user's language, unless the API is slow to tell it.
 */
export const verifyPin = async (environment: Environment, user: User, pin: string): Promise<void> => {
    const {data} = await getInstance(environment).post<{access_token: string; refresh_token: string}>('/api/v2/token', {
        username: user.username,
        pincode: pin,
    });

    setTokens(data.access_token, data.refresh_token, true);
    setPolyshoppenUser(user);

    await Promise.race([
        loadLocale(environment),
        new Promise<void>(resolve => window.setTimeout(resolve, localeTimeout)),
    ]);
};

/** The message for a failed `verifyPin`, shown in the PIN dialog. */
export const getPinErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error) && 401 === error.response?.status) {
        return t('pin.errorInvalid');
    }

    return t('pin.errorFailed', {message: (error as Error)?.message});
};

/**
 * An idle terminal must not hold a valid token: drop the tokens, but keep the user so the PIN can be asked again.
 */
export const suspendSession = (): void => clearTokens();

/**
 * Switch the API the terminal talks to. A token is only valid on the API that issued it: the user logs in again on the
 * other environment (the router sends every environment change to the login page).
 */
export const switchEnvironment = (environment: Environment): void => {
    if (environment === getEnvironment()) {
        return;
    }

    leaveBreadcrumb('Environment switched', {environment});
    clearSession();
    setEnvironment(environment);
};

/** There is no server side session: forgetting the tokens is enough (this also resets the environment to production). */
export const logout = (): void => clearSession();

export const handleLogout = (): void => {
    logout();
    redirectTo('/login');
};
